import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../../config/supabaseClient';
import queryClient from '../../../config/queryClient';
import axiosInstance from '../../../config/axiosInstance';
import { useAuth } from '../../../context/AuthContext';

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    '& fieldset': { borderColor: '#334155' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
};

export default function PokerStudyLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  const [view, setView] = useState<'signIn' | 'resetPassword'>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fromPath =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/poker-study';

  if (!isLoading && isAuthenticated) {
    return <Navigate to={fromPath} replace />;
  }

  async function handleLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      // IND-058: se vacía antes de entrar: después cancelaría lo que la sesión
      // nueva ya está pidiendo (p. ej. el rol industrial del operario).
      queryClient.clear();
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        const message = error.message.toLowerCase();

        if (message.includes('email not confirmed')) {
          setErrorMessage(
            'Tu cuenta aún no ha sido verificada. Revisa el correo de confirmación para activar el acceso.',
          );
        } else if (message.includes('invalid login credentials')) {
          setErrorMessage('El correo electrónico o la contraseña son incorrectos.');
        } else {
          setErrorMessage(error.message || 'No se pudo iniciar sesión.');
        }

        return;
      }

      navigate(fromPath, { replace: true });
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo iniciar sesión.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordResetRequest(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const { data } = await axiosInstance.post('/auth/request-password-reset', {
        email: email.trim(),
      });

      setSuccessMessage(data.message || 'Te hemos enviado instrucciones para recuperar el acceso.');
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || 'No se pudo procesar la solicitud.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f172a',
        display: 'flex',
        alignItems: 'stretch',
      }}
    >
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pt: 4,
          pb: { xs: 8, md: 12 },
        }}
      >
        <Button
          variant="text"
          onClick={() => navigate('/poker')}
          sx={{
            alignSelf: 'flex-start',
            mb: 2,
            color: '#94a3b8',
            textTransform: 'none',
            fontWeight: 800,
            '&:hover': {
              color: '#f8fafc',
              bgcolor: 'transparent',
            },
          }}
        >
          ← Volver a PokerStudy
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: 4,
            boxShadow: '0 0 40px rgba(0, 0, 0, 0.4)',
          }}
        >
          <Avatar
            sx={{
              m: 1,
              bgcolor: alpha('#38bdf8', 0.1),
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.3)',
            }}
          >
            <LockOutlinedIcon />
          </Avatar>

          <Typography
            component="h1"
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#f8fafc',
              mt: 1,
              mb: 1,
              textAlign: 'center',
            }}
          >
            {view === 'signIn' ? 'Acceso PokerStudy' : 'Recuperar Acceso'}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              mb: 3,
              textAlign: 'center',
            }}
          >
            {view === 'signIn'
              ? 'Ingresa tus credenciales para continuar al sistema de estudio.'
              : 'Introduce tu email para recibir instrucciones de recuperación.'}
          </Typography>

          {errorMessage ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                width: '100%',
                mb: 3,
                borderColor: '#f87171',
                color: '#f87171',
                '& .MuiAlert-icon': { color: '#f87171' },
              }}
            >
              <AlertTitle>Error de acceso</AlertTitle>
              {errorMessage}
            </Alert>
          ) : null}

          {successMessage ? (
            <Alert
              severity="success"
              variant="outlined"
              sx={{
                width: '100%',
                mb: 3,
                borderColor: '#4ade80',
                color: '#4ade80',
                '& .MuiAlert-icon': { color: '#4ade80' },
              }}
            >
              <AlertTitle>Solicitud enviada</AlertTitle>
              {successMessage}
            </Alert>
          ) : null}

          {view === 'signIn' ? (
            <Box component="form" onSubmit={handleLogin} noValidate sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo electrónico"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isSubmitting}
                sx={inputSx}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={isSubmitting}
                sx={inputSx}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  bgcolor: '#38bdf8',
                  color: '#0f172a',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#0ea5e9' },
                  '&.Mui-disabled': { bgcolor: '#334155', color: '#94a3b8' },
                }}
              >
                {isSubmitting ? 'Autenticando...' : 'Entrar a PokerStudy'}
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  mt: 2,
                  gap: 2,
                }}
              >
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setView('resetPassword');
                    setErrorMessage(null);
                    setSuccessMessage(null);
                  }}
                  sx={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    '&:hover': { color: '#fcd34d' },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>

                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/poker')}
                  sx={{
                    color: '#38bdf8',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { color: '#7dd3fc' },
                  }}
                >
                  Ver landing
                </Link>
              </Box>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handlePasswordResetRequest}
              noValidate
              sx={{ mt: 1, width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo electrónico"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isSubmitting}
                sx={inputSx}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  bgcolor: '#fcd34d',
                  color: '#0f172a',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#fbbf24' },
                  '&.Mui-disabled': { bgcolor: '#334155', color: '#94a3b8' },
                }}
              >
                {isSubmitting ? 'Procesando...' : 'Enviar enlace'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => {
                    setView('signIn');
                    setErrorMessage(null);
                    setSuccessMessage(null);
                  }}
                  sx={{
                    color: '#94a3b8',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    '&:hover': { color: '#fff' },
                  }}
                >
                  ← Volver al login
                </Link>
              </Box>
            </Box>
          )}
        </Paper>

        <Typography
          variant="caption"
          sx={{
            mt: 4,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.2)',
          }}
        >
          PokerStudy · Protected by Secure Auth Protocol v2.0
        </Typography>
      </Container>
    </Box>
  );
}